import { useCallback, useMemo, useState } from "react";

/**
 * 價格摘要 Hook
 * 管理現金/信用卡收款金額，並提供差額提示與驗證。
 *
 * @param {Object} options
 * @param {number} [options.orderTotal] - 訂單總額（折抵後）
 * @param {Object} [options.refs] - 輸入框的 refs
 */
const usePriceSummary = ({ orderTotal = 0, refs = {} } = {}) => {
	const { cashPaymentRef, cardPaymentRef } = refs || {};
	const targetTotal = Number(orderTotal) || 0;

	// 使用 null 代表「尚未輸入」，數字（包含 0）代表「已輸入」
	const [cashPayment, setCashPayment] = useState(null);
	const [cardPayment, setCardPayment] = useState(null);

	// 現金收款變更處理
	const onCashPaymentChange = useCallback(
		(e, value) => {
			// 空值視為尚未輸入
			if (value === "" || value === null || value === undefined) {
				setCashPayment(null);
			} else {
				setCashPayment(Number(value));
			}
		},
		// eslint-disable-next-line
		[],
	);

	// 信用卡收款變更處理
	const onCardPaymentChange = useCallback(
		(e, value) => {
			if (value === "" || value === null || value === undefined) {
				setCardPayment(null);
			} else {
				setCardPayment(Number(value));
			}
		},
		// eslint-disable-next-line
		[],
	);

	/**
	 * 從 refs 同步輸入框的值到 state
	 * 用於處理使用者直接按 Enter 送出表單的情況
	 */
	const syncFromRefs = useCallback(() => {
		if (cashPaymentRef?.current?.getResult) {
			const raw = cashPaymentRef.current.getResult();
			const cashNum = raw === "" || raw === null || raw === undefined ? null : Number(raw);
			if (cashNum !== cashPayment) {
				setCashPayment(cashNum);
			}
		}

		if (cardPaymentRef?.current?.getResult) {
			const raw = cardPaymentRef.current.getResult();
			const cardNum = raw === "" || raw === null || raw === undefined ? null : Number(raw);
			if (cardNum !== cardPayment) {
				setCardPayment(cardNum);
			}
		}
	}, [cashPaymentRef, cardPaymentRef, cashPayment, cardPayment]);

	/**
	 * 從 refs 讀取最新的付款金額（不更新 state）
	 * 用於即時取得輸入框的值
	 */
	const getPaymentFromRefs = useCallback(() => {
		let cash = cashPayment;
		let card = cardPayment;

		if (cashPaymentRef?.current?.getResult) {
			const raw = cashPaymentRef.current.getResult();
			cash = raw === "" || raw === null || raw === undefined ? null : Number(raw);
		}

		if (cardPaymentRef?.current?.getResult) {
			const raw = cardPaymentRef.current.getResult();
			card = raw === "" || raw === null || raw === undefined ? null : Number(raw);
		}

		return { cash, card };
	}, [cashPaymentRef, cardPaymentRef, cashPayment, cardPayment]);

	// 計算已收款總額（null 視為 0）
	const paidAmount = useMemo(
		() => {
			return (Number(cashPayment ?? 0) || 0) + (Number(cardPayment ?? 0) || 0);
		},
		// eslint-disable-next-line
		[cashPayment, cardPayment],
	);

	// 計算收款差額狀態（提示性，不阻止送出）
	const balanceStatus = useMemo(
		() => {
			// 尚未輸入收款金額（兩個都是 null）不顯示狀態
			if (cashPayment === null || cardPayment === null) return null;


			const diff = paidAmount - targetTotal;
			if (diff === 0) {
				return { severity: "success", color: "success", message: "收款金額與訂單總額相符" };
			}

			if (diff < 0) {
				// 整張訂單有欠款（實收 < 應付）
				return { severity: "warning", color: "warning", message: `收款尚差 $${Math.abs(diff)}` };
			}

			// 整張訂單溢收（實收 > 應付）
			return { severity: "info", color: "info", message: `收款超出 $${diff}` };
		},
		// eslint-disable-next-line
		[paidAmount, targetTotal, cashPayment, cardPayment],
	);

	/**
	 * 取得收款結果
	 */
	const getPaymentResults = useCallback(
		() => {
			// 如果有傳入 refs，優先從 refs 讀取最新值
			if (cashPaymentRef || cardPaymentRef) {
				const { cash, card } = getPaymentFromRefs();
				return {
					cashPayment: cash,
					cardPayment: card,
				};
			}
			// 否則從 state 讀取（null 視為尚未輸入）
			return {
				cashPayment,
				cardPayment,
			};
		},
		// eslint-disable-next-line
		[cashPayment, cardPayment, cashPaymentRef, cardPaymentRef, getPaymentFromRefs],
	);

	/**
	 * 取得收款驗證狀態
	 * - isValid: 是否允許送出
	 * - needsConfirmation: 是否需要額外確認（0 元）
	 */
	const getPaymentMatchStatus = useCallback(
		() => {
			let currentPaidAmount = paidAmount;

			// 如果有傳入 refs，優先從 refs 讀取最新值進行驗證
			if (cashPaymentRef || cardPaymentRef) {
				const { cash, card } = getPaymentFromRefs();
				currentPaidAmount = (Number(cash) || 0) + (Number(card) || 0);
			}

			// 前端只做基本防呆，實際金額是否合法交由後端驗證
			const diff = currentPaidAmount - targetTotal;

			// 1) 溢收：不允許送出
			if (targetTotal > 0 && diff > 0) {
				return { isValid: false, needsConfirmation: false, message: "不可超收，請確認" };
			}

			// 2) 欠款：需要額外確認
			if (targetTotal > 0 && diff < 0) {
				return { isValid: true, needsConfirmation: true, message: `收款尚差 $${Math.abs(diff)}，確定送出嗎？` };
			}



			// 3) 其他情況：交由後端做完整驗證
			return { isValid: true, needsConfirmation: false, message: "" };
		},
		// eslint-disable-next-line
		[paidAmount, targetTotal, cashPaymentRef, cardPaymentRef, getPaymentFromRefs],
	);

	return {
		cashPayment,
		cardPayment,
		paidAmount,
		balanceStatus,
		onCashPaymentChange,
		onCardPaymentChange,
		syncFromRefs,
		getPaymentResults,
		getPaymentMatchStatus,
	};
};

export default usePriceSummary;
